import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "../ui/Button"
import { Dialog } from "@/components/ui/Dialog"

import { popDialog, pushDialog, resetDialogs } from "@/store/dialogs-state"
import { PresetItem } from "@/store/presets/presets-service"
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectSeparator,
} from "../ui/Select"
import { useAtomValue } from "jotai"
import { presetByGroupMapAtom } from "@/store/presets/presets-state"
import { InputDialog } from "./InputDialog"

export const MovePresetDialog = (props: {
	preset: PresetItem
	onConfirm: (group: string) => void
}) => {
	const { t } = useTranslation("components")
	const [group, setGroup] = useState(props.preset.group)
	const [newGroup, setNewGroup] = useState<string | undefined>(undefined)
	const presetGroupMap = useAtomValue(presetByGroupMapAtom)

	const presetGroups = Object.keys(presetGroupMap).concat(newGroup ? [newGroup] : [])

	const showCreateNewGroupDialog = () => {
		pushDialog(InputDialog, {
			title: t("presetDialogs.createNewGroupTitle"),
			buttonTitle: "Create",
			initialInput: "",
			confirmWithEnter: true,
			onConfirm: (group: string) => {
				setNewGroup(group)
				setGroup(group)
				popDialog()
			},
			trackingViewName: "create_preset_group_dialog",
		})
	}

	return (
		<Dialog trackingViewName={"move_preset_dialog"} contentSize="sm-h-auto" disableClose>
			<div className="flex w-full flex-col space-y-4">
				<div className="text-start text-md font-bold">{t("presetDialogs.movePreset")}</div>
				<div className="flex flex-row items-center space-x-1">
					<div className="text-sm">{t("presetDialogs.group")}</div>
					<Select
						trackingLabel="preset_group"
						value={group}
						onValueChange={(value) => {
							if (value === "create_new") {
								showCreateNewGroupDialog()
								return
							}
							setGroup(value)
						}}
					>
						<SelectTrigger className="text-sm">{group}</SelectTrigger>
						<SelectContent side="top">
							<SelectGroup>
								<SelectItem
									key="create_new_key"
									value="create_new"
									className="text-sm"
								>
									{t("presetDialogs.newGroup")}
								</SelectItem>
								<SelectSeparator />
								{presetGroups.map((group) => (
									<SelectItem key={group} value={group} className="text-sm">
										{group}
									</SelectItem>
								))}
							</SelectGroup>
						</SelectContent>
					</Select>
				</div>

				<div className="flex w-full flex-row justify-between">
					<div className="flex flex-row space-x-2">
						<Button
							trackingLabel="create_new"
							aria-label={t("presetDialogs.ariaCreateNew")}
							size="sm"
							onClick={showCreateNewGroupDialog}
						>
							{t("presetDialogs.createNewGroup")}
						</Button>
					</div>
					<div className="flex flex-row space-x-2">
						<Button trackingLabel="cancel" onClick={resetDialogs}>
							{t("common:action.cancel")}
						</Button>
						<Button
							disabled={!group || group === props.preset.group}
							onClick={() => {
								if (!group) return
								resetDialogs()
								props.onConfirm(group)
							}}
							variant="primary"
							trackingLabel={"ok"}
						>
							{t("presetDialogs.ok")}
						</Button>
					</div>
				</div>
			</div>
		</Dialog>
	)
}
